<template>
    <div>
        <b-modal
            v-model="isOpen"
            size="md"
            modal-class="vue-need-bootstrap"
            lazy
            centered
            hide-footer
            :title="getTitle()"
            @close="closeModal"
            @cancel="closeModal"
            @ok="closeModal"
            @hide="closeModal"
        >
            <template v-if="entityType === 'S'" #modal-header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <h5 class="modal-title mb-0">{{ getTitle() }}</h5>
                    <div class="d-flex align-items-center">
                        <b-button close class="ml-2 close" @click="closeModal">×</b-button>
                    </div>
                </div>
            </template>
            <template v-if="!handlingEntityVersions">
                <div class="py-1 overflow-auto" style="max-height: 340px">
                    <div class="mb-3">
                        <div class="hist-elem">
                            <p class="entity-name">{{ displayName }}</p>
                        </div>
                        <div class="hist-id">{{ entityId }}</div>
                    </div>
                    <div v-if="auditEntries.length > 0" id="timeline">
                        <div
                            v-for="(entry, index) in auditEntries"
                            :key="entry.entityId + entry.date"
                            class="pb-3"
                            :class="index == auditEntries.length - 1 ? 'timeline-item-last' : 'timeline-item-common'"
                        >
                            <div class="mb-1 op-text">{{ parseOperation(entry.operation) }}</div>
                            <div class="mb-1 user-text">
                                {{ `${entry.user?.Name ?? ''} (${entry.user?.Username})` }}
                            </div>
                            <div class="mb date-text">{{ getFormattedDate(entry.date) }}</div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer row pb-0">
                    <b-button
                        class="ml-auto btn back-btn btn-secondary"
                        size="sm"
                        variant="secondary"
                        @click="closeModal"
                    >
                        Close
                    </b-button>
                    <b-button
                        v-if="entityHandlesVersioning"
                        size="sm"
                        variant="primary"
                        @click="handleEntityVersions()"
                    >
                        <font-awesome-icon :icon="['fas', 'fa-history']" />
                        Restore Previous Version
                    </b-button>
                </div>
            </template>
            <template v-else-if="entityHandlesVersioning && handlingEntityVersions">
                <restore-entity-version
                    :data-dd-action-name="`Restore ${parseEntity()} Version`"
                    :entity-type="entityType"
                    :entity-id="entityId"
                    :entity-name="displayName ?? ''"
                    :get-version="getVersion()"
                    :list-versions="listVersions()"
                    :restore-version="restoreVersion()"
                    @go-back="handleHistory"
                    @entity-version-restored="entityVersionRestored"
                />
            </template>
        </b-modal>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { DateFormat, formatDate } from '@/utils/dates';
import { useStore } from '@/store';

import { AuditEntity, AuditLogWithUser, AuditOperation } from '@extTypes/auditLog/types';
import { OptionSetDto } from '@extTypes/options/optionSet';
import { Settings } from '@extTypes/settings/settings';
import { EntityVersionRestored } from '@extTypes/versioning';

import { saveSettings } from '@/api/shopify/settings';
import { getAuditEntriesWithUsers } from '@/api/customily/auditLog';
import { getOptionSetFromVersion, listOptionSetVersions } from '@/api/customily/optionSet';
import { getSettingsFromVersion, listSettingsVersions } from '@/api/customily/settings';

import RestoreEntityVersion from '@/components/AuditLog/RestoreEntityVersion.vue';

const props = defineProps<{
    value: boolean;
    entityType: AuditEntity;
    entityId?: string | number;
    entityCreatedDate?: Date | string;
    displayName?: string;
    isEntityIdClientId?: boolean;
}>();

const listVersions = () => {
    if (props.entityType === AuditEntity.SETTING) {
        return (id: string | number) => listSettingsVersions(String(id));
    }
    if (props.entityType === AuditEntity.OPTION_SET) {
        return (id: string | number) => listOptionSetVersions(String(id));
    }
    throw new Error('Unsupported entity type');
};

const getVersion = () => {
    if (props.entityType === AuditEntity.SETTING) {
        return (key: string, versionId: string) => getSettingsFromVersion(key, versionId);
    }
    if (props.entityType === AuditEntity.OPTION_SET) {
        return (key: string, versionId: string) => getOptionSetFromVersion(key, versionId);
    }
    throw new Error('Unsupported entity type');
};

const restoreVersion = (): ((data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>) => {
    if (props.entityType === AuditEntity.SETTING) {
        return (async (data: Settings.Settings, id: string | number) => {
            await saveSettings(data, String(id), false);
        }) as (data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>;
    }
    if (props.entityType === AuditEntity.OPTION_SET) {
        // The actual restoration logic for OPTION_SET is handled in the parent component's
        // onOptionSetVersionRestored handler. This stub is intentionally left empty for type compatibility.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return (async (data: OptionSetDto, id: string) => {
            return Promise.resolve();
        }) as (data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>;
    }
    throw new Error('Unsupported entity type');
};

const entityHandlesVersioning = computed(
    () => props.entityType === AuditEntity.SETTING || props.entityType === AuditEntity.OPTION_SET
);
const loading = ref(true);
const handlingEntityVersions = ref(false);
const auditEntries = ref<AuditLogWithUser[]>([]);
const { state } = useStore();

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'close'): void;
    (event: 'on-version-restored', data: EntityVersionRestored): void;
}>();

const closeModal = () => {
    handlingEntityVersions.value = false;
    emit('close');
};

const isOpen = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        emit('input', value);
    }
});

const entityVersionRestored = (data: EntityVersionRestored) => {
    emit('on-version-restored', data);
    closeModal();
};

watch(
    () => props.entityId,
    () => {
        if (props.entityId || props.isEntityIdClientId) {
            getAuditEntriesWithUsers(
                props.entityType,
                `${props.isEntityIdClientId ? state.user?.client_id : props.entityId}`
            )
                .then(async (data) => {
                    if (!data.some((e) => e.operation == 'C') && props) {
                        data.unshift({
                            entity: props.entityType,
                            entityId: `${props.isEntityIdClientId ? state.user?.client_id : props.entityId}`,
                            operation: AuditOperation.CREATE,
                            performedByUserId: state.user?.customilyUser.Id!,
                            date: props.entityCreatedDate ? new Date(props.entityCreatedDate) : '',
                            impersonatedUserId: null,
                            action: 'null',
                            details: null,
                            user: {
                                Id: 0,
                                Name: state.user?.customilyUser.Name ?? '',
                                Username: state.user?.customilyUser.Username ?? ''
                            }
                        });
                    }

                    auditEntries.value = data.reverse();

                    loading.value = false;
                })
                .catch((error) => {
                    console.error('Error getting audit entries with users', JSON.stringify(error));
                    loading.value = false;
                });
        }
    },
    { immediate: true }
);

const parseOperation = (operation: AuditOperation): string => {
    switch (operation) {
        case AuditOperation.CREATE:
            return 'Created';
        case AuditOperation.RESTORE:
            return 'Restored';
        case AuditOperation.UPDATE:
            return 'Updated';
        case AuditOperation.DELETE:
            return 'Deleted';
        default:
            return 'Unknown';
    }
};

const parseEntity = (): string => {
    switch (props.entityType) {
        case AuditEntity.LIBRARY:
            return 'Library';
        case AuditEntity.OPTION_SET:
            return 'Option Set';
        case AuditEntity.PRODUCT:
            return 'Product';
        case AuditEntity.TEMPLATE:
            return 'Template';
        case AuditEntity.SETTING:
            return 'Setting';
        default:
            return 'Unknown';
    }
};

const getTitle = (): string => {
    return `${parseEntity()} history`;
};

const getFormattedDate = (date: Date | string): string => {
    if (date === '') return date;
    return formatDate(date, DateFormat.MMMddYYYYTime);
};

const handleHistory = () => {
    handlingEntityVersions.value = false;
};

const handleEntityVersions = () => {
    handlingEntityVersions.value = true;
};
</script>

<style lang="scss" scoped>
.close:hover {
    background-color: unset !important;
    border-color: unset !important;
}

#timeline .timeline-item-common {
    border-left: 1px solid #bfbfbf;
    position: relative;
    padding-inline-start: 20px;
    margin-left: 4px;
}

#timeline .timeline-item-last {
    position: relative;
    padding-inline-start: 20px;
    margin-left: 4px;
}

#timeline .timeline-item-common:after {
    position: absolute;
    display: block;
    top: 0;
    border-radius: 145px;
    border: 1px solid var(--Gray-scale-gray, #bfbfbf);
    background: var(--Gray-scale-white, #fefefe);
    left: -5px;
    height: 10px;
    width: 10px;
    content: '';
}

#timeline .timeline-item-last:after {
    position: absolute;
    display: block;
    top: 0;
    border-radius: 145px;
    border: 1px solid var(--Gray-scale-gray, #bfbfbf);
    background: var(--Gray-scale-white, #fefefe);
    left: -4px;
    height: 10px;
    width: 10px;
    content: '';
}

.hist-elem {
    display: flex;
    color: var(--Gray-scale-gray-darkest, #333);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;

    .entity-name {
        width: 100%;
        margin-bottom: 5px;
    }
}

.hist-id {
    color: var(--Primary-primary, #7258e0);
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.op-text {
    color: #8a8a8a;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.user-text {
    color: var(--Gray-scale-gray-darkest, #333);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.date-text {
    color: #565656;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}
</style>
